/**
 * The words of the feedback hub: type tiles, the questions each type asks, topics,
 * statuses and impact. One place, so the dialog, "My feedback", the triage view and the
 * tests read the same text. Sentence case, no long dash, "sales" for the people working
 * the mailbox.
 */

import type {
  FeedbackFrequency,
  FeedbackHubTopic,
  FeedbackImpact,
  FeedbackIntent,
  FeedbackKind,
  FeedbackStatus,
  FeedbackSurface,
} from "@/lib/api";

export interface KindTile {
  kind: FeedbackKind;
  title: string;
  hint: string;
}

/** The five tiles at the top of the dialog (section 4). */
export const KIND_TILES: readonly KindTile[] = [
  { kind: "bug", title: "Something broke", hint: "A button, a page or a number does not work" },
  {
    kind: "model",
    title: "Wrong result from the assistant",
    hint: "Wrong customer, item, price, reply, translation or kind of mail",
  },
  { kind: "idea", title: "Idea or feature request", hint: "Something new that would help" },
  {
    kind: "change",
    title: "Change how something works",
    hint: "Something exists but should work differently",
  },
  { kind: "question", title: "Question", hint: "You are not sure how something works" },
];

/** Short name of a type, for lists and badges. */
export const KIND_LABEL: Record<FeedbackKind, string> = {
  bug: "Something broke",
  model: "Wrong result",
  idea: "Idea",
  change: "Change",
  question: "Question",
};

export interface TextQuestion {
  key: string;
  label: string;
  required: boolean;
}

/** The text questions of each type, in screen order (the model type adds its own block). */
export const KIND_QUESTIONS: Record<FeedbackKind, readonly TextQuestion[]> = {
  bug: [
    { key: "did", label: "What were you doing?", required: true },
    { key: "happened", label: "What happened?", required: true },
    { key: "expected", label: "What did you expect?", required: false },
  ],
  model: [
    { key: "wrong", label: "Describe the wrong result", required: false },
    { key: "note", label: "Anything else we should know?", required: false },
  ],
  idea: [
    { key: "problem", label: "What problem would it solve?", required: true },
    { key: "suggestion", label: "What do you suggest?", required: false },
  ],
  change: [
    { key: "today", label: "How does it work today?", required: true },
    { key: "wanted", label: "How should it work?", required: true },
    { key: "why", label: "Why?", required: false },
  ],
  question: [{ key: "question", label: "Your question", required: true }],
};

export const TOPIC_LABEL: Record<FeedbackHubTopic, string> = {
  customer: "Customer",
  item: "Item",
  quantity: "Quantity",
  price: "Price",
  margin: "Margin",
  classification: "Kind of mail",
  reply_draft: "Reply",
  translation: "Translation",
  other: "Something else",
};

/** The topics the picker offers; margin only to people who see the margin. */
export function topicOptions(seeMargin: boolean): FeedbackHubTopic[] {
  const all: FeedbackHubTopic[] = [
    "customer",
    "item",
    "quantity",
    "price",
    "margin",
    "classification",
    "reply_draft",
    "translation",
    "other",
  ];
  return seeMargin ? all : all.filter((topic) => topic !== "margin");
}

/** The question of the "what is right" field for a topic. */
export const RIGHT_LABEL: Record<FeedbackHubTopic, string> = {
  customer: "The right customer number or name",
  item: "The right item number",
  quantity: "The right quantity",
  price: "The right price",
  margin: "What is wrong with the margin?",
  classification: "What kind of mail is it?",
  reply_draft: "What should it say or do?",
  translation: "What should it say or do?",
  other: "What should it say or do?",
};

export const INTENT_OPTIONS: readonly { value: FeedbackIntent; label: string }[] = [
  { value: "rfq_quote", label: "Inquiry" },
  { value: "order_place", label: "Purchase order" },
  { value: "other", label: "Something else" },
  { value: "not_request", label: "Not a request" },
];

export const IMPACT_LABEL: Record<FeedbackImpact, string> = {
  blocks: "Blocks my work",
  slows: "Slows me down",
  nice: "Nice to have",
};

export const FREQUENCY_LABEL: Record<FeedbackFrequency, string> = {
  several_daily: "Several times a day",
  daily: "Daily",
  weekly: "Weekly",
  rarely: "Rarely",
};

export const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: "New",
  under_review: "Under review",
  planned: "Planned",
  in_progress: "In progress",
  done: "Done",
  wont_do: "Won't do",
  duplicate: "Duplicate",
  noted: "Noted",
};

export type BadgeTone = "neutral" | "accent" | "success" | "danger" | "warning" | "info";

export const STATUS_TONE: Record<FeedbackStatus, BadgeTone> = {
  new: "info",
  under_review: "accent",
  planned: "accent",
  in_progress: "warning",
  done: "success",
  wont_do: "neutral",
  duplicate: "neutral",
  noted: "neutral",
};

/** The statuses a triager may set by hand; `noted` is set by the system only. */
export const TRIAGE_STATUSES: readonly FeedbackStatus[] = [
  "new",
  "under_review",
  "planned",
  "in_progress",
  "done",
  "wont_do",
  "duplicate",
];

export const SURFACE_LABEL: Record<FeedbackSurface, string> = {
  shell: "App",
  case: "Case",
  line: "Case line",
  customer: "Customer card",
  reply_draft: "Reply",
  translation: "Mail translation",
  dashboard: "Dashboard",
  queue: "Queue",
  learned: "Learned corrections",
  addin: "Outlook add-in",
  help: "Help",
  other: "Other",
};

/** Hint above the text fields. */
export const NO_MAIL_IN_TEXT_HINT =
  "Do not paste customer mail or prices into the text. I2Q attaches the case for you.";

/** Under the mail tick. */
export const MAIL_TEXT_HINT = "Tick this only when the mail itself is the problem.";

/** Under the model behaviour form. */
export const MODEL_FIX_NOTE =
  "To correct this case now, use Confirm, Pick item or No item on the row, or Not them and Find customer on the customer line. Feedback tells the build team; it does not change the case.";

export const SAVED_TOAST = "Thanks. You can follow it under My feedback.";

/** Shown wherever nothing leaves the app. */
export const NOTHING_LEAVES_NOTE =
  "Feedback stays in Norican I2Q. It creates no mail, no Teams message and no ticket.";

/**
 * Decision D7 (19 Oct 2026): the dialog keeps unsent text per case in this browser and
 * asks before typed text is thrown away (a sales person typed for two minutes, pressed
 * Cancel and lost it). "Close and keep the text" exists only on a case: without a case
 * there is no place to keep it.
 */
export const DRAFT_WORDS = {
  restored: "Your unsent text for this case is back.",
  startAgain: "Start again",
  askTitle: "Close without saving?",
  askOnCase: "Your text is not saved as feedback yet. You can keep it for this case.",
  askNoCase: "Your text is not saved as feedback yet. It is lost when you close.",
  keepWriting: "Keep writing",
  closeKeep: "Close and keep the text",
  discard: "Discard the text",
} as const;

/** "FB-121". */
export function feedbackRef(id: number): string {
  return `FB-${id}`;
}

/** The headline of an item: its title, else its type. */
export function itemHeadline(item: { title?: string | null; kind: FeedbackKind }): string {
  const title = (item.title ?? "").trim();
  return title === "" ? KIND_LABEL[item.kind] : title;
}

/** The label of a body key of a kind, for the "what you sent" block. */
export function bodyKeyLabel(kind: FeedbackKind, key: string): string {
  return KIND_QUESTIONS[kind].find((question) => question.key === key)?.label ?? key;
}

/** "Several times a day" style label of anything, or null for an unknown value. */
export function labelOf<T extends string>(table: Record<T, string>, value: unknown): string | null {
  return typeof value === "string" && Object.hasOwn(table, value) ? table[value as T] : null;
}
